/**
 * Form Validators - Framework-agnostic validation functions
 * Pure TypeScript functions that can be used in Angular, React, or any framework
 */

export interface ValidationResult {
  valid: boolean;
  error?: string;
  errorKey?: string;
}

/**
 * Validation Functions
 * Returns ValidationResult for framework-agnostic usage
 */

export const validators = {
  /**
   * Check if value is required (not empty)
   */
  required(value: any): ValidationResult {
    const valid = value !== null && value !== undefined && value !== "";
    return {
      valid,
      error: valid ? undefined : "This field is required",
      errorKey: valid ? undefined : "required",
    };
  },

  /**
   * Check email format
   */
  email(value: string): ValidationResult {
    if (!value) return { valid: true };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const valid = emailRegex.test(value);
    return {
      valid,
      error: valid ? undefined : "Invalid email format",
      errorKey: valid ? undefined : "email",
    };
  },

  /**
   * Check minimum length
   */
  minLength(value: string, min: number): ValidationResult {
    if (!value) return { valid: true };

    const valid = value.length >= min;
    return {
      valid,
      error: valid ? undefined : `Minimum length is ${min} characters`,
      errorKey: valid ? undefined : "minLength",
    };
  },

  /**
   * Check maximum length
   */
  maxLength(value: string, max: number): ValidationResult {
    if (!value) return { valid: true };

    const valid = value.length <= max;
    return {
      valid,
      error: valid ? undefined : `Maximum length is ${max} characters`,
      errorKey: valid ? undefined : "maxLength",
    };
  },

  /**
   * Check minimum numeric value
   */
  min(value: number, min: number): ValidationResult {
    if (value === null || value === undefined) return { valid: true };

    const valid = value >= min;
    return {
      valid,
      error: valid ? undefined : `Minimum value is ${min}`,
      errorKey: valid ? undefined : "min",
    };
  },

  /**
   * Check maximum numeric value
   */
  max(value: number, max: number): ValidationResult {
    if (value === null || value === undefined) return { valid: true };

    const valid = value <= max;
    return {
      valid,
      error: valid ? undefined : `Maximum value is ${max}`,
      errorKey: valid ? undefined : "max",
    };
  },

  /**
   * Check pattern match (regex)
   */
  pattern(value: string, pattern: string | RegExp): ValidationResult {
    if (!value) return { valid: true };

    const regex = typeof pattern === "string" ? new RegExp(pattern) : pattern;
    const valid = regex.test(value);
    return {
      valid,
      error: valid ? undefined : "Invalid format",
      errorKey: valid ? undefined : "pattern",
    };
  },

  /**
   * Check for special characters (< >)
   * Used in Master Data forms
   */
  noSpecialChars(value: string): ValidationResult {
    if (!value) return { valid: true };

    const charRegexp = /[<>]/;
    const valid = !charRegexp.test(value);
    return {
      valid,
      error: valid ? undefined : "Special characters < > are not allowed",
      errorKey: valid ? undefined : "invalidName",
    };
  },

  /**
   * Check for numeric only
   */
  numeric(value: string): ValidationResult {
    if (!value) return { valid: true };

    const valid = /^[0-9]+$/.test(value);
    return {
      valid,
      error: valid ? undefined : "Only numeric characters allowed",
      errorKey: valid ? undefined : "numeric",
    };
  },

  /**
   * Check for alphabetic characters only (with spaces)
   */
  alphabetic(value: string): ValidationResult {
    if (!value) return { valid: true };

    const valid = /^[a-zA-Z ]+$/.test(value);
    return {
      valid,
      error: valid ? undefined : "Only alphabetic characters allowed",
      errorKey: valid ? undefined : "alphabetic",
    };
  },

  /**
   * Check for alphanumeric characters only
   */
  alphanumeric(value: string): ValidationResult {
    if (!value) return { valid: true };

    const valid = /^[a-zA-Z0-9]+$/.test(value);
    return {
      valid,
      error: valid ? undefined : "Only alphanumeric characters allowed",
      errorKey: valid ? undefined : "alphanumeric",
    };
  },

  /**
   * Check for decimal number (up to 2 decimal places)
   */
  decimal(value: string | number): ValidationResult {
    if (!value && value !== 0) return { valid: true };

    const valid = /^[0-9]+(\.[0-9]{1,2})?$/.test(String(value));
    return {
      valid,
      error: valid
        ? undefined
        : "Invalid decimal format (max 2 decimal places)",
      errorKey: valid ? undefined : "decimal",
    };
  },

  /**
   * Check for username format (letters and underscores, optional space)
   */
  username(value: string): ValidationResult {
    if (!value) return { valid: true };

    const valid = /^[A-Za-z_]+( [A-Za-z_]+)?$/.test(value);
    return {
      valid,
      error: valid ? undefined : "Invalid username format",
      errorKey: valid ? undefined : "username",
    };
  },
};

/**
 * Pattern Constants
 * Common regex patterns used across the application
 */
export const ValidationPatterns = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  NUMERIC: /^[0-9]+$/,
  ALPHABETIC: /^[a-zA-Z ]+$/,
  ALPHANUMERIC: /^[a-zA-Z0-9]+$/,
  DECIMAL: /^[0-9]+(\.[0-9]{1,2})?$/,
  DECIMAL_NEGATIVE: /^-?[0-9]\d*(\.\d{1,2})?$/,
  SPECIAL_CHARS: /[<>]/,
  USERNAME: /^[A-Za-z_]+( [A-Za-z_]+)?$/,
  STATE_CODE: /^[0-9]*$/,
};

/**
 * Validation Error Messages
 * Centralized error messages for consistency
 */
export const ValidationMessages = {
  REQUIRED: "This field is required",
  EMAIL: "Invalid email format",
  MIN_LENGTH: (min: number) => `Minimum length is ${min} characters`,
  MAX_LENGTH: (max: number) => `Maximum length is ${max} characters`,
  MIN: (min: number) => `Minimum value is ${min}`,
  MAX: (max: number) => `Maximum value is ${max}`,
  PATTERN: "Invalid format",
  NO_SPECIAL_CHARS: "Special characters < > are not allowed",
  NUMERIC: "Only numeric characters allowed",
  ALPHABETIC: "Only alphabetic characters allowed",
  ALPHANUMERIC: "Only alphanumeric characters allowed",
  DECIMAL: "Invalid decimal format (max 2 decimal places)",
  USERNAME: "Invalid username format",
};
